<script setup>
import { ElMessage, ElMessageBox } from "element-plus";
import { ref, reactive, computed } from "vue";
import RealVideo from "./realVideo.vue";
import ReplayVideo from "./replayVideo.vue";

import {
  queryCameraByPlantId,
  syncCamera,
  queryCameraLink,
} from "@/api/stationBaseInfo";

const props = defineProps(["deptId"]);

const emits = defineEmits(["returnTotal"]);

// 视频播放ref
const playRef = ref(null);

// 视频回放ref
const rePlayRef = ref(null);

// 表格遮罩
const loading = ref(false);

// 摄像头配置列表
const cameraList = ref([]);

// 摄像头配置总数
const listTotal = ref(0);

// 摄像头弹窗ref
const cameraFormRef = ref(null);

// 查询参数
const listQueryParams = reactive({
  pageNum: 1,
  pageSize: 10,
  deptId: undefined,
});

// 摄像头弹窗参数
const cameraDialog = reactive({
  open: false,
  title: "编辑摄像头",
});

const cameraPlayDialog = reactive({
  open: false,
  title: "",
});
// 摄像头编辑表单
const cameraForm = reactive({
  id: undefined,
  cameraName: undefined,
  hikId: undefined,
  channel: undefined,
  remarks: undefined,
});

// 摄像头编辑校验
const cameraRules = reactive({
  cameraName: [
    { required: true, message: "摄像头名称不能为空", trigger: "blur" },
  ],
  hikId: [{ required: true, message: "摄像头ID不能为空", trigger: "blur" }],
  channel: [{ required: true, message: "通道号不能为空", trigger: "blur" }],
});

// 视频播放参数
const playOptions = reactive({
  id: undefined,
  title: undefined,
  url: undefined,
});

// 视频回放对话框
const cameraReplayDialog = reactive({
  open: false,
  title: "",
});

// 视频回放参数
const replayOptions = reactive({
  hikId: undefined,
});

// 同步摄像头
const handleInit = () => {
  syncCamera(props.deptId).then((res) => {
    if (res.code == "2000") {
      ElMessage.success("同步成功");
      getCameraList();
    }
  });
};

// 获取摄像头列表
const getCameraList = () => {
  loading.value = true;
  queryCameraByPlantId({
    deptId: props.deptId,
  })
    .then((res) => {
      cameraList.value = res.data.records;
      listTotal.value = res.data.total;
    })
    .finally(() => {
      loading.value = false;
      emits("returnTotal", listTotal.value);
    });
};

// 编辑摄像头
const handleEdit = (row) => {
  cameraDialog.open = true;

  Object.keys(cameraForm).forEach((key) => {
    cameraForm[key] = row[key];
  });
};

// 删除摄像头
const handleDelete = (row) => {
  ElMessageBox.confirm(
    `是否确认删除名称为"${row.cameraName}"的摄像头?`,
    "警告",
    {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "warning",
    }
  )
    .then(() => {})
    .catch(() => {});
};

// 视频播放
const handlePlay = (row) => {
  queryCameraLink(row.id).then((res) => {
    playOptions.id = row.id;
    playOptions.title = row.cameraName;
    playOptions.url = res.data.url;

    cameraPlayDialog.open = true;
  });
};

// 视频回放
const handleReplay = (row) => {
  replayOptions.hikId = row.hikId;
  cameraReplayDialog.open = true;
};

// 保存摄像头配置
const cameraSubmit = () => {
  cameraFormRef.value?.validate((valid) => {
    if (valid) {
      //
    }
  });
};

// 暴露函数
defineExpose({
  getCameraList,
});
</script>

<template>
  <div id="cameraPage">
    <el-row :gutter="10" class="mb8" justify="end" style="float: right">
      <el-col :span="1.5">
        <el-button type="success" plain size="small" @click="handleInit()"
          >同步摄像头</el-button
        >
      </el-col>
    </el-row>
    <el-table
      :header-cell-style="{ 'text-align': 'center' }"
      :cell-style="{ 'text-align': 'center' }"
      v-loading="loading"
      :data="cameraList"
      row-key="id"
      default-expand-all
      border
    >
      <el-table-column prop="cameraName" label="摄像头名称" />
      <el-table-column prop="hikId" label="摄像头ID" />
      <el-table-column prop="channel" label="通道号" width="160px" />
      <el-table-column label="操作">
        <template #default="{ row }">
          <el-button
            link
            type="primary"
            icon="el-icon-video-camera"
            @click="handlePlay(row)"
            >播放</el-button
          >
          <el-button
            link
            type="warning"
            icon="el-icon-video-camera"
            @click="handleReplay(row)"
            >视频回放</el-button
          >
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      v-show="listTotal > 0"
      :total="listTotal"
      v-model:currentPage="listQueryParams.pageNum"
      @update:currentPage="getCameraList"
      layout="->, prev, pager, next"
    />
    <!-- 视频播放对话框 -->
    <div class="videoPlay">
      <el-dialog
        :title="cameraPlayDialog.title"
        v-model="cameraPlayDialog.open"
        v-if="cameraPlayDialog.open"
        width="900px"
        :destroy-on-close="true"
        :show-close="true"
      >
        <real-video :options="playOptions" />
      </el-dialog>
    </div>
    <!-- 视频回放对话框 -->
    <div class="videoReplay">
      <el-dialog
        :title="cameraReplayDialog.title"
        v-model="cameraReplayDialog.open"
        v-if="cameraReplayDialog.open"
        width="900px"
        :destroy-on-close="true"
        :show-close="false"
      >
        <replay-video :reparams="replayOptions" />
      </el-dialog>
    </div>
  </div>
</template>

<style lang="scss" scoped>
#cameraPage {
  .el-link {
    padding: 0 5px;
  }
}
.videoPlay {
  ::v-deep .el-dialog {
    .el-dialog__header {
      padding-top: 0;
      padding-bottom: 0;
    }
    .el-dialog__body {
      padding: 0;
    }
  }
}
.videoReplay {
  ::v-deep .el-dialog {
    // margin-left: 27.8%;
    .el-dialog__header {
      padding-top: 0;
      padding-bottom: 0;
    }
    .el-dialog__body {
      padding: 3px;
    }
  }
}
.mb8 {
  margin-bottom: 8px;
}
</style>
